<i18n src="@/locales/models/inventory_item.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/service_provider/warehouse/movements/show_warehouse.yml"
  lang="yaml"
></i18n>
<template>
  <div class="mt-4">
    <h1 class="page-title">
      <i class="fa-solid fa-boxes"></i>
      <span class="space-icon"></span>
      {{ $t("labels.inventory_items_title") }}
    </h1>

    <div class="flex flex-row justify-end">
      <button type="button" @click.prevent="goToNewItem" class="btn-white">
        <i class="fa-solid fa-plus"></i>
        <span class="space-icon"></span>
        {{ $t("labels.new_item_button") }}
      </button>
    </div>

    <div class="flex flex-row-reverse mt-4 gap-2">
      <white-button
        v-if="filtered"
        label="Limpiar"
        @click.prevent="clearFilters"
      />
      <white-button label="Filtrar" @click.prevent="filterItems" />

      <select-form
        class="w-56"
        :placeholder="$t('model.inventory_item.inventory_unit')"
        :options="inventory_units"
        v-model="filterParams.inventory_unit_id"
        :is_invalid="isInvalidSelect('inventory_unit')"
      />

      <input
        type="text"
        v-model.trim="filterParams.name"
        :placeholder="$t('model.inventory_item.name')"
        class="shadow-sm focus:ring-[#5c7a2e] focus:border-[#5c7a2e] block sm:text-sm border-slate-300 rounded-md"
      />

      <input
        type="text"
        v-model.trim="filterParams.item_code"
        :placeholder="$t('model.inventory_item.item_code')"
        class="shadow-sm focus:ring-[#5c7a2e] focus:border-[#5c7a2e] block sm:text-sm border-slate-300 rounded-md"
      />
    </div>

    <div class="mt-2 flex flex-col">
      <ag-grid-vue
        style="width: 100%; height: 600px"
        class="ag-theme-alpine"
        @grid-ready="onGridReady"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="inventory_items"
      >
      </ag-grid-vue>
    </div>

    <table-paginator
      v-model="current_page"
      v-if="total_rows > rows_per_page"
      :total_rows="total_rows"
      :rows_per_page="rows_per_page"
    >
    </table-paginator>
  </div>
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";

import Requester from "@/views/service_provider/requesters/warehouse/movements/show_warehouse.js";
import ManageRequester from "@/views/service_provider/requesters/warehouse/movements/manage_inventory_items.js";

import TableActions from "@/components/views/layout/ag_grid/TableActions.vue";
import TablePaginator from "@/components/views/layout/TablePaginator.vue";

export default {
  name: "PHProductionBasicContainerExportShowProcessContainers",
  components: { AgGridVue, TablePaginator },
  data() {
    return {
      inventory_items: [],
      current_page: 1,
      total_rows: 0,
      rows_per_page: 12,
      inventory_units: [],
      filtered: false,
      filterParams: {
        item_code: "",
        name: "",
        inventory_unit_id: null
      },
      defaultColDef: {
        sortable: true,
        filter: false,
        floatingFilter: false
      },
      columnDefs: [
        {
          headerName: this.$t("model.inventory_item.item_code"),
          field: "item_code",
          flex: 1
        },
        {
          headerName: this.$t("model.inventory_item.name"),
          field: "name",
          flex: 1
        },
        {
          headerName: this.$t("model.inventory_item.stock"),
          field: "stock",
          flex: 1
        },
        {
          headerName: this.$t("model.inventory_item.description"),
          field: "description",
          flex: 2
        },
        {
          headerName: this.$t("model.inventory_item.inventory_unit"),
          field: "inventory_unit.name",
          flex: 1
        },
        {
          headerName: "",
          flex: 1,
          cellRendererSelector: params => {
            return {
              component: TableActions,
              params: {
                edit_label: "",
                editEvent: this.goToEditItem,
                show_destroy: true,
                destroy_label: "",
                destroy_title: this.$t("labels.destroy_item_button_title", {
                  item: params.data.name
                }),
                destroy_message: this.$t("labels.destroy_item_button_message", {
                  item: params.data.name
                }),
                destroy_label_confirm: this.$t(
                  "labels.destroy_item_button_label_confirm"
                ),
                destroyEvent: this.destroy
              }
            };
          }
        }
      ]
    };
  },
  props: {
    service_provider_id: { required: true },
    warehouse_id: { required: true }
  },

  created() {
    this.fetchInventoryItems();
    this.fetchInventoryUnits();
  },

  methods: {
    fetchInventoryItems: async function () {
      let response = await Requester.fetchInventoryItems(
        this.service_provider_id,
        this.warehouse_id,
        {
          current_page: this.current_page,
          rows_per_page: this.rows_per_page,
          ...this.filterParams
        }
      );
      this.total_rows = response.data.total_rows;
      this.inventory_items = response.data.records;
    },

    fetchInventoryUnits: async function () {
      let response = await Requester.fetchInventoryUnits(
        this.service_provider_id,
        this.warehouse_id
      );

      this.inventory_units = response.data;
    },

    clearFilters: async function () {
      this.filterParams = {
        item_code: "",
        name: "",
        inventory_unit_id: null
      };
      this.filtered = false;
      this.current_page = 1;
      await this.fetchInventoryItems();
    },

    filterItems: async function () {
      if (
        !this.filterParams.item_code &&
        !this.filterParams.name &&
        !this.filterParams.inventory_unit_id
      ) {
        return;
      }

      this.filtered = true;
      this.current_page = 1;
      await this.fetchInventoryItems();
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    },

    sizeToFit() {
      this.gridApi.sizeColumnsToFit({ defaultMinWidth: 150 });
    },

    destroy: async function (item) {
      try {
        const index = this.inventory_items.indexOf(item);
        let response = await ManageRequester.destroy(
          this.service_provider_id,
          this.warehouse_id,
          item.id
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.inventory_items.splice(index, 1);
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
    },

    goToNewItem: function () {
      this.$router.push({
        name: this.routeName("new_item"),
        params: {
          service_provider_id: this.service_provider_id,
          warehouse_id: this.warehouse_id
        }
      });
    },

    goToEditItem: function (item) {
      this.$router.push({
        name: this.routeName("edit_item"),
        params: {
          service_provider_id: this.service_provider_id,
          warehouse_id: this.warehouse_id,
          id: item.id
        }
      });
    }
  },

  watch: {
    current_page: async function () {
      this.load_data = false;
      await this.fetchInventoryItems();
      this.load_data = true;
    }
  }
};
</script>
